import React, {useCallback, useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {Box, useBreakpointValue} from '@chakra-ui/react'
import {useGoogleTagManager, windowGetDevice} from '../../custom-api/hooks/use-google-tag-manager'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    get_layer_page_category_and_subcategory,
    retrieve_status_code_gtm,
    virtual_page_view,
} from '../../custom-api/layers/default'
import {hashSHA256} from '../../utils/utils'
import md5 from 'md5'
import {useLocaleCountry, useLocaleLanguage} from '../../hooks/use-locale-custom'
import useUtils from '../../custom-hooks/use-utils-helper'

const GtmPageView = () => {
    const location = useLocation()

    // Used only to check if the realPageview event has been pushed for the current location (first location)
    const isRealPageviewPushed = useRef(false)

    // Track if we've already sent the event with complete customer data (for registered users)
    const hasCustomerDataBeenSent = useRef(false)

    // Since the real page virtual url changes, we need to keep track of the last pushed URL
    const realPageviewPushedUrl = useRef(null)

    // Since the real page virtual url changes, we need to keep track of the last pushed URL
    const virtualPageviewPushedUrl = useRef(null)

    const locationChangeEvent = useRef(null)

    const initialPath = useRef(null)

    const hasLeftInitialPath = useRef(false)

    const {data: customer, isLoading: isLoadingCustomer} = useCurrentCustomer()
    const {data: basket, isLoading: basketIsLoading} = useCurrentBasket()

    const country = useLocaleCountry()
    const language = useLocaleLanguage()

    const currentPage = useUtils().currentPage(location.pathname)
    const {isProductDetail, isCategory} = currentPage
    const sendEvent = !isProductDetail && !isCategory

    const deviceType = useBreakpointValue({base: null, sm: 'm', md: 't', lg: 'd'})

    const {addLayer, addLayerAtPositionZero, positionZeroEventName} = useGoogleTagManager()

    const {customParams} = getConfig()

    const hasCustomerData = useCallback(
        async (customer, basket) => {
            const customerFirstName =
                customer?.firstName || localStorage.getItem('guestCustomerFirstName')
            const customerLastName =
                customer?.lastName || localStorage.getItem('guestCustomerLastName')
            const email =
                customer?.email ||
                basket?.customerInfo?.email ||
                localStorage.getItem('guestCustomerEmail')
            const id =
                customer?.c_gigyaId ||
                basket?.c_gigyaGuestId ||
                localStorage.getItem('c_gigyaGuestId')

            if (!email && !id) return
            else
                return {
                    emailSHA256: email ? await hashSHA256(email) : null,
                    emailMD5: email ? md5(email) : null,
                    userId: id,
                    name: customerFirstName,
                    surname: customerLastName,
                    email,
                }
        },
        [
            customer?.firstName,
            customer?.lastName,
            customer?.email,
            customer?.customerId,
            basket?.customerInfo?.email,
            basket?.customerInfo?.customerId,
        ],
    )

    useEffect(() => {
        window.dataLayer = window.dataLayer || []
        const currentPath = `${location.pathname}${location?.search}`

        if (realPageviewPushedUrl.current !== currentPath) {
            realPageviewPushedUrl.current = currentPath
        }

        ;(async () => {
            const {page_category, page_subcategory} =
                get_layer_page_category_and_subcategory(currentPage)

            const virtualUrl = `${window?.location?.protocol}//${customParams?.currentHost}${location?.pathname}${location?.search}`
            const customerData = await hasCustomerData(customer, basket)

            // Wait for customer to finish loading, unless we already know they're a guest
            if (isLoadingCustomer && !customer.isGuest) {
                return
            }

            // For registered users, additionally wait until we have actual customer data
            if (customer.isRegistered && (!customerData?.email || !customer?.email)) {
                return
            }

            // For guest users, we proceed once customer is loaded (even with minimal/no data)
            const currentPath = `${location.pathname}${location?.search}`

            // For registered users: only fire if we haven't sent data yet
            // For guest users: fire only once per page
            const shouldFire =
                !isRealPageviewPushed.current ||
                isRealPageviewPushed.current !== currentPath ||
                (customer.isRegistered && customerData?.email && !hasCustomerDataBeenSent.current)

            if (shouldFire) {
                addLayerAtPositionZero(
                    virtual_page_view.bind(this, {
                        event: positionZeroEventName,
                        virtualUrl,
                        pageCountry: country,
                        pageLanguage: language,
                        device: deviceType || windowGetDevice(window?.innerWidth),
                        ...customerData,
                        pageCategory: page_category,
                        pageSubCategory: page_subcategory,
                        statusCode: retrieve_status_code_gtm(),
                        c_gigyaId:
                            customer?.c_gigyaId ||
                            basket?.c_gigyaGuestId ||
                            localStorage.getItem('c_gigyaGuestId'),
                    }),
                )

                //console.debug('[SFCC DEBUG] GTM Pageview Event with data')
                // Set the flag to prevent duplicates
                isRealPageviewPushed.current = currentPath

                // Track that we've sent customer data for registered users
                if (customer.isRegistered && customerData?.email) {
                    hasCustomerDataBeenSent.current = true
                }
            }

            realPageviewPushedUrl.current = currentPath

            if (locationChangeEvent.current !== virtualUrl) {
                setTimeout(() => {
                    if (sendEvent) {
                        window.dispatchEvent(new Event('locationchange'))
                        locationChangeEvent.current = virtualUrl
                    }
                }, 150)
            }
        })()
    }, [
        isLoadingCustomer,
        basketIsLoading,
        deviceType,
        basket?.customerInfo?.email,
        basket?.customerInfo?.customerId,
        location.pathname,
        location.search,
        customer?.email,
        customer?.customerId,
        customer?.firstName,
        customer?.lastName,
        customer?.c_gigyaId,
        basket?.c_gigyaGuestId,
    ])

    useEffect(() => {
        window.dataLayer = window.dataLayer || []
        const currentPath = `${location.pathname}${location?.search}`
        //update the virtualUrl in Product is choosen from the Search modal
        const isFromISP = localStorage.getItem('fromISP')
        localStorage.removeItem('fromISP')
        let virtualUrl = `${window?.location?.protocol}//${customParams?.currentHost}${location?.pathname}${location?.search}`

        if (!initialPath.current) {
            initialPath.current = currentPath
        }

        if (currentPath !== initialPath.current) {
            hasLeftInitialPath.current = true
        }

        if (!hasLeftInitialPath.current && currentPath === initialPath.current) {
            return
        }

        if (!isRealPageviewPushed.current) return
        if (!basketIsLoading || !isLoadingCustomer || customer?.isGuest) {
            const isVirtualPushed =
                virtualPageviewPushedUrl.current && virtualPageviewPushedUrl.current === currentPath

            if (!isVirtualPushed) {
                const {page_category, page_subcategory} =
                    get_layer_page_category_and_subcategory(currentPage)

                if (page_category === 'detail' && isFromISP) {
                    const url = new URL(virtualUrl)
                    url.searchParams.set('fromISP', 'true')
                    virtualUrl = url.pathname + url.search
                }

                ;(async () => {
                    const customerData = await hasCustomerData(customer, basket)

                    addLayer(
                        virtual_page_view.bind(this, {
                            virtualUrl,
                            pageCountry: country,
                            pageLanguage: language,
                            device: deviceType || windowGetDevice(window?.innerWidth),
                            ...customerData,
                            pageCategory: page_category,
                            pageSubCategory: page_subcategory,
                            statusCode: retrieve_status_code_gtm(),
                            c_gigyaId:
                                customer?.c_gigyaId ||
                                basket?.c_gigyaGuestId ||
                                localStorage.getItem('c_gigyaGuestId'),
                        }),
                    )

                    virtualPageviewPushedUrl.current = currentPath
                    if (locationChangeEvent.current !== virtualUrl) {
                        setTimeout(() => {
                            if (sendEvent) {
                                window.dispatchEvent(new Event('locationchange'))
                                locationChangeEvent.current = virtualUrl
                            }
                        }, 150)
                    }
                })()
            }
        }
    }, [
        location.pathname,
        isLoadingCustomer,
        basketIsLoading,
        deviceType,
        isRealPageviewPushed.current,
        basket?.customerInfo?.email,
        basket?.customerInfo?.customerId,
        customer?.email,
        customer?.customerId,
        basket?.c_gigyaGuestId,
    ])

    useEffect(() => {
        const trustedOrigins = [
            'https://google.com',
            'https://googletagmanager.com',
            'https://www.google.com',
            'https://www.googletagmanager.com',
            'https://sgtm.delonghi.com',
            'https://sgtm.braunhousehold.com',
            'https://sgtm.kenwoodworld.com',
            'https://sgtm.nutribullet.com',
            'https://cdns.eu1.gigya.com',
            `https://${customParams?.currentHost}`,
            window.location.origin,
        ]

        window.addEventListener('message', (event) => {
            if (!trustedOrigins.includes(event.origin)) {
                console.warn('Message received from untrusted origin:', event.origin)
                return
            }
            if (event.data && event.data.action === 'setGTMCode') {
                // eslint-disable-next-line no-undef
                injectCode(event.data.code, event.data.position)
            }
            var dataLayerData = event.data
            if (dataLayerData && dataLayerData.event_source === 'custom_pixel_dataLayer_replace') {
                window.dataLayer = window.dataLayer || []
                window.dataLayer = dataLayerData.data
            }

            if (dataLayerData && dataLayerData.event_source === 'customPixel') {
                if (
                    dataLayerData.data[0].event === 'gtm.dom' ||
                    dataLayerData.data[0].event === 'gtm.load'
                ) {
                    return
                }
                window.dataLayer.push(dataLayerData.data[0])
            }
        })
    }, [])

    return <Box data-testid="gtm-page-view-component" />
}

export default GtmPageView
